<template>
  <div v-if="loaded">
    <v-container class="mxw-800 mx-auto py-12 px-4">
      <template v-if="section === 'finish'">
        <div class="px-4">
          <h1 class="mb-4">
            {{ $bct(quizSchema.name) }}
          </h1>

          <div class="d-flex align-start mb-4">
            <div>
              {{ $bct(gradeScaleDisplayText) }}
            </div>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="mx-auto mb-6 ta-center">
          <div class="fs-22 fw-500 mb-0">
            {{ $bct(quizSchema.name) }}
          </div>

          <div class="fs-16 c-light-black">
            <span class="me-1">
              {{ $bct('Question') }}
            </span>

            <span>
              {{ stepCount >= stepTotal ? stepTotal - 1 : stepCount }} / {{ stepTotal - 1 }}
            </span>
          </div>
        </div>

        <div
          ref="progressContainerRef"
          :aria-label="$bct('Progress indicator - question') + ' ' + stepCount"
          class="px-4 focus-invisible"
          tabindex="0"
          aria-live
        >
          <v-progress-linear
            v-model="progress"
            :indeterminate="!loaded"
            class="focus-invisible mb-4"
            color="primary"
          />
        </div>
      </template>

      <div class="px-4">
        <QuestionSet
          v-model="quiz"
          @back="backFromQuestion($event)"
          @next="forwardFromQuestion($event)"
          :processing="processing"
          :questions="validQuestions"
          :schema="quizSchema.definition"
          :section="section"
          :transition-name="transitionName"
          color="white"
          key-name="question"
          border
        />

        <template v-if="section == 'finish'">
          <QuestionSet
            v-model="quiz"
            :questions="validQuestions"
            :schema="quizSchema.definition"
            color="white"
            key-name="question"
            border
            condensed
            dense
            display-answer
            expanded
            hide-actions
            readonly
            show-passing
          />

          <v-row>
            <v-col class="d-flex justify-space-between">
              <v-btn
                v-if="!quiz.passing"
                @click="retake"
                color="primary"
                size="x-large"
              >
                <span>
                  {{ $bct('Retake quiz') }}
                </span>
              </v-btn>

              <v-btn
                :to="{ name: 'CourseShow', params: { courseId: lessonCompletion.course_id } }"
                color="primary"
                size="x-large"
              >
                <span>
                  {{ $bct('Return to course') }}
                </span>
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </div>
    </v-container>
  </div>
</template>

<script setup>
import { useRoute, useRouter } from 'vue-router';

import QuestionSet from '@/shared/components/form/QuestionSet.vue';
import useApi from '@/shared/composables/useApi';
import useQuestionable from '@/shared/composables/useQuestionable';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useStepper from '@/shared/composables/useStepper';

const $bct = inject('translator');

const { api: Api } = useApi();
const { updateQuery } = useRouterHelper();
const route = useRoute();
const router = useRouter();

const gradeScaleDisplayText = ref(null);
const lesson = ref(null);
const lessonCompletion = ref(null);
const quiz = ref(null);
const quizSchema = ref(null);
const processing = ref(false);
const progressContainerRef = ref(null);
const ownerIdRef = ref(route.params.quizSchemaId);

const { progress, section, stepBack, stepForward, stepCount, stepTotal, transitionName } =
  useStepper({
    processing,
    route,
    updateQuery,
  });

const { loadQuestions, questionsLoaded, validQuestions, validateAnswers } = useQuestionable({
  ownerDataTypeRef: ref('Schema'),
  ownerIdRef,
  syncedObjectRef: quiz,
});

const loaded = computed(() => {
  return lessonCompletion.value && quizSchema.value && quiz.value && questionsLoaded.value;
});

watch(
  loaded,
  async (newValue, oldValue) => {
    if (newValue && newValue !== oldValue) {
      stepCount.value = parseInt(route.query.step, 10);
      if (!stepTotal.value) await validateAnswers();

      recalculateStepTotal();
      if (route.query.section) {
        section.value = route.query.section;
        progress.value = (stepCount.value / stepTotal.value) * 100;
        if (route.query.section !== 'finish' && progress.value) {
          progressContainerRef.value?.focus();
        }
      } else {
        await router.push({ query: { section: 'question-0', step: 1 } });
      }
    }
  },
  { immediate: true },
);

watch(
  () => route.query.section,
  async (newValue) => {
    stepCount.value = parseInt(route.query.step, 10);
    if (!stepTotal.value) {
      await validateAnswers();
    }
    recalculateStepTotal();
    section.value = newValue ? newValue : 'question-0';
    progress.value = (stepCount.value / stepTotal.value) * 100;

    // Focus the progress bar for accessibility when on question sections
    if (newValue !== 'finish' && progress.value) {
      progressContainerRef.value?.focus();
    }
  },
);

async function backFromQuestion(index) {
  processing.value = true;
  await saveQuiz();
  await validateAnswers();
  recalculateStepTotal();

  if (index - 1 < 0) {
    await router.push({
      name: 'CourseShow',
      params: { courseId: lessonCompletion.value.course_id },
    });
  } else {
    stepBack(`question-${index - 1}`);
  }
}

async function createQuiz() {
  const response = await Api.manager.quiz.create({
    lesson_completion_id: route.params.lessonCompletionId,
    schema_id: route.params.quizSchemaId,
  });
  if (response.status === 201) {
    quiz.value = response.data;
    await loadQuestions();
  }
}

async function finish() {
  quiz.value.submitted = true;
  await saveQuiz();
  gradeScaleDisplayText.value = getGradeScaleDisplayText();
  stepForward('finish');
}

async function forwardFromQuestion(index) {
  processing.value = true;

  if (index + 1 >= validQuestions.value.length) {
    await finish();
  } else {
    quiz.value.submitted = false;
    await saveQuiz();
    await validateAnswers();
    recalculateStepTotal();
    stepForward(`question-${index + 1}`);
  }
}

function getGradeScaleDisplayText() {
  const sortedGradeScales = lesson.value.grade_scale.sort((gradeScale1, gradeScale2) =>
    gradeScale1.min_score > gradeScale2.min_score ? -1 : 1,
  );

  const grade =
    sortedGradeScales.find((gradeScale) => quiz.value.score >= gradeScale.min_score) ||
    sortedGradeScales.at(-1);

  return grade.display_text;
}

async function load() {
  loadQuizSchema();
  await loadLessonCompletion();
  await loadLesson();
  await createQuiz();
}

async function loadLesson() {
  const response = await Api.publicApi.organization.lesson.get(lessonCompletion.value.lesson_id);
  lesson.value = response.data;
}

async function loadLessonCompletion() {
  const response = await Api.lessonCompletion.get(route.params.lessonCompletionId);
  lessonCompletion.value = response.data;
}

function loadQuizSchema() {
  Api.publicApi.organization.schema.get(route.params.quizSchemaId, (response) => {
    quizSchema.value = response.data;
  });
}

function recalculateStepTotal() {
  stepTotal.value = validQuestions.value.length + 1;
  progress.value = (stepCount.value / stepTotal.value) * 100;
}

async function retake() {
  quiz.value = null;
  await router.push({
    name: 'LessonQuiz',
    params: {
      lessonCompletionId: lessonCompletion.value.id,
      quizSchemaId: route.params.quizSchemaId,
    },
    query: { retake: true },
  });
  stepCount.value = 0;
  await load();
}

async function saveQuiz() {
  const { data } = await Api.manager.quiz.promiseUpdate(quiz.value.id, quiz.value);
  quiz.value = data;
}

onMounted(load);
</script>
